import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'

const ProjectContext = createContext()

const STORAGE_KEY = 'tdo-current-project'
const PROJECT_ID_KEY = 'tdo-current-project-id'
const PROJECT_ROLE_KEY = 'tdo-current-project-role'

// Default project structure
const createDefaultProject = () => ({
  id: crypto.randomUUID(),
  name: 'New Project',
  description: '',
  startDate: new Date().toISOString().split('T')[0],
  timeUnit: 'weeks',
  xAxisStart: 0,
  xAxisEnd: 12,
  distanceLabels: ['Start', 'Phase 1', 'Phase 2', 'Phase 3', 'Complete'],
  flows: [],
  dependencies: [],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
})

// Create default flow
const createFlow = (name, color) => ({
  id: crypto.randomUUID(),
  name,
  color,
  hidden: false,
  checkpoints: []
})

// Create default checkpoint
const createCheckpoint = (name, time, distance) => ({
  id: crypto.randomUUID(),
  name,
  time,
  distance,
  status: 'pending',
  type: 'checkpoint',
  estimatedDuration: 0,
  subtasks: []
})

// Load project from localStorage
const loadFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const data = JSON.parse(saved)
      return { ...createDefaultProject(), ...data }
    }
  } catch (e) {
    console.error('Failed to load project from localStorage:', e)
  }
  return createDefaultProject()
}

// Save project to localStorage
const saveToStorage = (project) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(project))
  } catch (e) {
    console.error('Failed to save project to localStorage:', e)
  }
}

export function ProjectProvider({ children }) {
  const [project, setProject] = useState(loadFromStorage)
  const [isDirty, setIsDirty] = useState(false)
  const [selectedFlow, setSelectedFlow] = useState(null)
  const [selectedCheckpoint, setSelectedCheckpoint] = useState(null)
  const [timeView, setTimeView] = useState('compact') // 'compact' or 'proportional'
  const [zoom, setZoom] = useState(100)
  const [activeView, setActiveView] = useState('process') // 'process', 'dependencies', 'description'
  const [currentProjectId, setCurrentProjectId] = useState(() => {
    try {
      return localStorage.getItem(PROJECT_ID_KEY) || null
    } catch {
      return null
    }
  })
  const [projectRole, setProjectRole] = useState(() => {
    try {
      return localStorage.getItem(PROJECT_ROLE_KEY) || null
    } catch {
      return null
    }
  })
  const [saveStatus, setSaveStatus] = useState('saved') // 'saving', 'saved', 'error'
  const [saveError, setSaveError] = useState(null) // last save-failure reason for UI tooltip
  const autoSaveTimeoutRef = useRef(null)
  const statusTimeoutRef = useRef(null)
  const projectRef = useRef(project)
  const projectRoleRef = useRef(projectRole)
  const saveInFlightRef = useRef(false)
  const retryCountRef = useRef(0)
  projectRef.current = project
  projectRoleRef.current = projectRole

  // Convex hooks
  const savedProjects = useQuery(api.projects.list) || []
  const createProject = useMutation(api.projects.create)
  const updateProjectMutation = useMutation(api.projects.update)
  const deleteProjectMutation = useMutation(api.projects.remove)

  // Auto-save when project changes
  useEffect(() => {
    saveToStorage(project)
  }, [project])

  // Persist currentProjectId to localStorage
  useEffect(() => {
    try {
      if (currentProjectId) {
        localStorage.setItem(PROJECT_ID_KEY, currentProjectId)
      } else {
        localStorage.removeItem(PROJECT_ID_KEY)
        localStorage.removeItem(PROJECT_ROLE_KEY)
      }
    } catch {}
  }, [currentProjectId])

  // Persist projectRole to localStorage
  useEffect(() => {
    try {
      if (projectRole) {
        localStorage.setItem(PROJECT_ROLE_KEY, projectRole)
      } else {
        localStorage.removeItem(PROJECT_ROLE_KEY)
      }
    } catch {}
  }, [projectRole])

  // Clear stale saveStatus when new edits are made, so the unsaved
  // indicator can show. Without this, there's a gap when saveStatus is
  // still 'saved' (2s display timeout) but isDirty is already true —
  // no status condition matches and the leaf disappears.
  // Don't clear 'saving' — that must stay until the save completes.
  useEffect(() => {
    if (isDirty && saveStatus && saveStatus !== 'saving') {
      setSaveStatus('')
    }
  }, [isDirty, saveStatus])

  // Auto-save to database function.
  // Uses projectRef to read current data (avoids stale closures).
  // Tracks in-flight saves to prevent concurrent mutations.
  // Only clears isDirty if no edits arrived during the save.
  const performAutoSave = useCallback(async () => {
    if (saveInFlightRef.current) return // prevent concurrent saves

    // Block saves when the user is a VIEWER — the mutation will reject
    // server-side; without this gate the indicator gets stuck on
    // "Unsaved changes" forever because isDirty never clears.
    if (projectRoleRef.current === 'VIEWER') {
      console.warn('[teddo-save] skipped: VIEWER role cannot save')
      setSaveError('Read-only access — your role on this project is VIEWER. Changes will not be saved.')
      setSaveStatus('error')
      return
    }

    saveInFlightRef.current = true

    // Clear any pending status-clear timeout
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current)
      statusTimeoutRef.current = null
    }

    // Snapshot the project we're about to save
    const snapshot = projectRef.current
    setSaveStatus('saving')
    console.log('[teddo-save] start', { projectId: currentProjectId, role: projectRoleRef.current })

    try {
      if (currentProjectId) {
        await updateProjectMutation({
          id: currentProjectId,
          name: snapshot.name,
          description: snapshot.description || '',
          data: JSON.stringify(snapshot),
        })
      } else {
        const newId = await createProject({
          name: snapshot.name,
          description: snapshot.description || '',
          data: JSON.stringify(snapshot),
        })
        setCurrentProjectId(newId)
        setProjectRole('OWNER')
      }

      retryCountRef.current = 0
      setSaveError(null)

      // Only clear isDirty if project hasn't changed since save started
      const snapshotMatched = projectRef.current === snapshot
      if (snapshotMatched) {
        setIsDirty(false)
      }
      console.log('[teddo-save] success', { snapshotMatched })
      // else: isDirty stays true → auto-save effect will trigger another save

      setSaveStatus('saved')
      statusTimeoutRef.current = setTimeout(() => {
        statusTimeoutRef.current = null
        setSaveStatus('')
      }, 2000)
    } catch (err) {
      console.error('[teddo-save] failed:', err)
      setSaveError(err?.message || String(err))
      setSaveStatus('error')

      // Retry up to 3 times with exponential backoff
      if (retryCountRef.current < 3) {
        retryCountRef.current += 1
        const delay = 1000 * Math.pow(2, retryCountRef.current) // 2s, 4s, 8s
        statusTimeoutRef.current = setTimeout(() => {
          statusTimeoutRef.current = null
          setSaveStatus('')
          // isDirty is still true, so the auto-save effect will re-trigger
        }, delay)
      } else {
        statusTimeoutRef.current = setTimeout(() => {
          statusTimeoutRef.current = null
          setSaveStatus('')
        }, 5000)
      }
    } finally {
      saveInFlightRef.current = false
    }
  }, [currentProjectId, createProject, updateProjectMutation])

  // Auto-save to database with debouncing.
  // Depends on `project` so the debounce timer resets on each edit.
  useEffect(() => {
    if (!isDirty || !currentProjectId) {
      return
    }

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }

    console.log('[teddo-save] scheduled in 3s', { projectId: currentProjectId, role: projectRoleRef.current })

    // Set new timeout for auto-save (3 seconds after last change)
    autoSaveTimeoutRef.current = setTimeout(() => {
      performAutoSave()
    }, 3000)

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [project, isDirty, currentProjectId, performAutoSave])

  // Project operations
  const updateProject = useCallback((updates) => {
    setProject(prev => ({
      ...prev,
      ...updates,
      updatedAt: new Date().toISOString()
    }))
    setIsDirty(true)
  }, [])

  const newProject = useCallback(() => {
    const newProj = createDefaultProject()
    setProject(newProj)
    setSelectedFlow(null)
    setSelectedCheckpoint(null)
    setCurrentProjectId(null)
    setProjectRole(null)
    setIsDirty(false)
  }, [])

  // Load project from imported data
  const loadProject = useCallback((data) => {
    const loaded = {
      ...createDefaultProject(),
      ...data,
      // Handle settings object from standalone format
      ...(data.settings ? {
        timeUnit: data.settings.timeUnit || 'weeks',
        startDate: data.settings.startDate || new Date().toISOString().split('T')[0],
        distanceLabels: data.settings.distanceLabels || [],
        xAxisStart: data.settings.gridSize?.x ? 0 : (data.xAxisStart || 0),
        xAxisEnd: data.settings.gridSize?.x || data.xAxisEnd || 12
      } : {})
    }
    setProject(loaded)
    setSelectedFlow(null)
    setSelectedCheckpoint(null)
    setCurrentProjectId(null)
    setProjectRole(null)
    setIsDirty(false)
  }, [])

  // Flow operations
  const addFlow = useCallback((name = 'New Flow', color = '#ffa559') => {
    const flow = createFlow(name, color)
    setProject(prev => ({
      ...prev,
      flows: [...prev.flows, flow],
      updatedAt: new Date().toISOString()
    }))
    setIsDirty(true)
    return flow
  }, [])

  const updateFlow = useCallback((flowId, updates) => {
    setProject(prev => ({
      ...prev,
      flows: prev.flows.map(f =>
        f.id === flowId ? { ...f, ...updates } : f
      ),
      updatedAt: new Date().toISOString()
    }))
    setIsDirty(true)
  }, [])

  const deleteFlow = useCallback((flowId) => {
    setProject(prev => ({
      ...prev,
      flows: prev.flows.filter(f => f.id !== flowId),
      dependencies: prev.dependencies.filter(d =>
        !prev.flows.find(f => f.id === flowId)?.checkpoints.some(c =>
          c.id === d.from || c.id === d.to
        )
      ),
      updatedAt: new Date().toISOString()
    }))
    if (selectedFlow === flowId) {
      setSelectedFlow(null)
      setSelectedCheckpoint(null)
    }
    setIsDirty(true)
  }, [selectedFlow])

  const toggleFlowVisibility = useCallback((flowId) => {
    setProject(prev => ({
      ...prev,
      flows: prev.flows.map(f =>
        f.id === flowId ? { ...f, hidden: !f.hidden } : f
      ),
      updatedAt: new Date().toISOString()
    }))
    setIsDirty(true)
  }, [])

  // Checkpoint operations
  const addCheckpoint = useCallback((flowId, name, time, distance) => {
    const checkpoint = createCheckpoint(name, time, distance)
    setProject(prev => ({
      ...prev,
      flows: prev.flows.map(f =>
        f.id === flowId
          ? { ...f, checkpoints: [...f.checkpoints, checkpoint] }
          : f
      ),
      updatedAt: new Date().toISOString()
    }))
    setIsDirty(true)
    return checkpoint
  }, [])

  const updateCheckpoint = useCallback((flowId, checkpointId, updates) => {
    setProject(prev => ({
      ...prev,
      flows: prev.flows.map(f =>
        f.id === flowId
          ? {
              ...f,
              checkpoints: f.checkpoints.map(c =>
                c.id === checkpointId ? { ...c, ...updates } : c
              )
            }
          : f
      ),
      updatedAt: new Date().toISOString()
    }))
    setIsDirty(true)
  }, [])

  const deleteCheckpoint = useCallback((flowId, checkpointId) => {
    setProject(prev => ({
      ...prev,
      flows: prev.flows.map(f =>
        f.id === flowId
          ? { ...f, checkpoints: f.checkpoints.filter(c => c.id !== checkpointId) }
          : f
      ),
      dependencies: prev.dependencies.filter(d =>
        d.from !== checkpointId && d.to !== checkpointId
      ),
      updatedAt: new Date().toISOString()
    }))
    if (selectedCheckpoint === checkpointId) {
      setSelectedCheckpoint(null)
    }
    setIsDirty(true)
  }, [selectedCheckpoint])

  // Subtask operations
  const addSubtask = useCallback((flowId, checkpointId, text) => {
    setProject(prev => ({
      ...prev,
      flows: prev.flows.map(f =>
        f.id === flowId
          ? {
              ...f,
              checkpoints: f.checkpoints.map(c =>
                c.id === checkpointId
                  ? { ...c, subtasks: [...(c.subtasks || []), { text, done: false }] }
                  : c
              )
            }
          : f
      ),
      updatedAt: new Date().toISOString()
    }))
    setIsDirty(true)
  }, [])

  const toggleSubtask = useCallback((flowId, checkpointId, subtaskIndex) => {
    setProject(prev => ({
      ...prev,
      flows: prev.flows.map(f =>
        f.id === flowId
          ? {
              ...f,
              checkpoints: f.checkpoints.map(c =>
                c.id === checkpointId
                  ? {
                      ...c,
                      subtasks: c.subtasks.map((s, i) =>
                        i === subtaskIndex ? { ...s, done: !s.done } : s
                      )
                    }
                  : c
              )
            }
          : f
      ),
      updatedAt: new Date().toISOString()
    }))
    setIsDirty(true)
  }, [])

  const updateSubtask = useCallback((flowId, checkpointId, subtaskIndex, newText) => {
    setProject(prev => ({
      ...prev,
      flows: prev.flows.map(f =>
        f.id === flowId
          ? {
              ...f,
              checkpoints: f.checkpoints.map(c =>
                c.id === checkpointId
                  ? {
                      ...c,
                      subtasks: c.subtasks.map((s, i) =>
                        i === subtaskIndex ? { ...s, text: newText } : s
                      )
                    }
                  : c
              )
            }
          : f
      ),
      updatedAt: new Date().toISOString()
    }))
    setIsDirty(true)
  }, [])

  const deleteSubtask = useCallback((flowId, checkpointId, subtaskIndex) => {
    setProject(prev => ({
      ...prev,
      flows: prev.flows.map(f =>
        f.id === flowId
          ? {
              ...f,
              checkpoints: f.checkpoints.map(c =>
                c.id === checkpointId
                  ? { ...c, subtasks: c.subtasks.filter((_, i) => i !== subtaskIndex) }
                  : c
              )
            }
          : f
      ),
      updatedAt: new Date().toISOString()
    }))
    setIsDirty(true)
  }, [])

  const reorderSubtasks = useCallback((flowId, checkpointId, fromIndex, toIndex) => {
    setProject(prev => ({
      ...prev,
      flows: prev.flows.map(f =>
        f.id === flowId
          ? {
              ...f,
              checkpoints: f.checkpoints.map(c =>
                c.id === checkpointId
                  ? {
                      ...c,
                      subtasks: (() => {
                        const newSubtasks = [...c.subtasks]
                        const [movedItem] = newSubtasks.splice(fromIndex, 1)
                        newSubtasks.splice(toIndex, 0, movedItem)
                        return newSubtasks
                      })()
                    }
                  : c
              )
            }
          : f
      ),
      updatedAt: new Date().toISOString()
    }))
    setIsDirty(true)
  }, [])

  // Dependency operations
  const addDependency = useCallback((from, to) => {
    setProject(prev => {
      // Prevent duplicates
      const exists = prev.dependencies.some(d => d.from === from && d.to === to)
      if (exists) return prev
      return {
        ...prev,
        dependencies: [...prev.dependencies, { from, to }],
        updatedAt: new Date().toISOString()
      }
    })
    setIsDirty(true)
  }, [])

  const removeDependency = useCallback((from, to) => {
    setProject(prev => ({
      ...prev,
      dependencies: prev.dependencies.filter(d => !(d.from === from && d.to === to)),
      updatedAt: new Date().toISOString()
    }))
    setIsDirty(true)
  }, [])

  const reorderCheckpoints = useCallback((flowId, fromIndex, toIndex) => {
    setProject(prev => ({
      ...prev,
      flows: prev.flows.map(f =>
        f.id === flowId
          ? {
              ...f,
              checkpoints: (() => {
                const newCheckpoints = [...f.checkpoints]
                const [movedItem] = newCheckpoints.splice(fromIndex, 1)
                newCheckpoints.splice(toIndex, 0, movedItem)
                return newCheckpoints
              })()
            }
          : f
      ),
      updatedAt: new Date().toISOString()
    }))
    setIsDirty(true)
  }, [])

  // Database operations
  // fetchUserProjects is now reactive via useQuery(api.projects.list) above
  const fetchUserProjects = useCallback(async () => {
    // No-op: savedProjects is now reactive via Convex useQuery
    return savedProjects
  }, [savedProjects])

  const saveProjectToDatabase = useCallback(async () => {
    // Clear any pending status-clear timeout
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current)
      statusTimeoutRef.current = null
    }
    // Cancel any pending auto-save since we're saving now
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
      autoSaveTimeoutRef.current = null
    }
    // Wait for any in-flight auto-save to finish
    while (saveInFlightRef.current) {
      await new Promise(r => setTimeout(r, 100))
    }
    saveInFlightRef.current = true

    const snapshot = projectRef.current
    setSaveStatus('saving')
    try {
      if (currentProjectId) {
        await updateProjectMutation({
          id: currentProjectId,
          name: snapshot.name,
          description: snapshot.description || '',
          data: JSON.stringify(snapshot),
        })
      } else {
        const newId = await createProject({
          name: snapshot.name,
          description: snapshot.description || '',
          data: JSON.stringify(snapshot),
        })
        setCurrentProjectId(newId)
        setProjectRole('OWNER')
      }
      if (projectRef.current === snapshot) {
        setIsDirty(false)
      }
      setSaveStatus('saved')
      statusTimeoutRef.current = setTimeout(() => {
        statusTimeoutRef.current = null
        setSaveStatus('')
      }, 2000)
      return { success: true }
    } catch (err) {
      console.error('Failed to save project:', err)
      setSaveStatus('error')
      statusTimeoutRef.current = setTimeout(() => {
        statusTimeoutRef.current = null
        setSaveStatus('')
      }, 5000)
      return { success: false, error: err.message }
    } finally {
      saveInFlightRef.current = false
    }
  }, [currentProjectId, createProject, updateProjectMutation])

  const loadProjectFromDatabase = useCallback(async (projectId) => {
    try {
      // We need to fetch the project data. Since useQuery can't be called
      // conditionally, we use the ConvexReactClient directly via fetchQuery workaround.
      // Instead, we use the projects already loaded from the list and fetch on-demand.
      // For detailed data, we'll set the ID and let a reactive query load it.
      setCurrentProjectId(projectId)
      setSelectedFlow(null)
      setSelectedCheckpoint(null)

      // Set role immediately from the already-loaded project list so the UI
      // (e.g. Share button) doesn't wait for the Convex get query to resolve.
      const projectFromList = savedProjects.find(p => p._id === projectId)
      if (projectFromList?.role) {
        setProjectRole(projectFromList.role)
      }

      // The actual data loading happens through a useEffect that watches currentProjectId
      return { success: true, projectId }
    } catch (err) {
      console.error('Failed to load project:', err)
      return { success: false, error: err.message }
    }
  }, [savedProjects])

  const deleteProjectFromDatabase = useCallback(async (projectId) => {
    try {
      await deleteProjectMutation({ id: projectId })

      // If we deleted the current project, create a new one
      if (projectId === currentProjectId) {
        newProject()
        setCurrentProjectId(null)
      }

      return { success: true }
    } catch (err) {
      console.error('Failed to delete project:', err)
      return { success: false, error: err.message }
    }
  }, [currentProjectId, newProject, deleteProjectMutation])

  // Load project data when currentProjectId changes (reactive via Convex)
  const currentProjectData = useQuery(
    api.projects.get,
    currentProjectId ? { id: currentProjectId } : "skip"
  )

  // Track whether we're doing the initial load for a project
  const initialLoadRef = useRef(null)

  useEffect(() => {
    if (!currentProjectData || !currentProjectData.data) return

    // Always load when switching to a new project (initial load)
    const isInitialLoad = initialLoadRef.current !== currentProjectId
    if (isInitialLoad) {
      initialLoadRef.current = currentProjectId
      setProject(currentProjectData.data)
      setProjectRole(currentProjectData.role || 'OWNER')
      setIsDirty(false)
      setActiveView('description') // Start on overview when opening any project
      return
    }

    // For reactive updates from Convex: only apply if we have no unsaved local changes.
    // This prevents the server's stale data from overwriting optimistic local edits
    // (e.g., toggling a task status) before the auto-save has completed.
    if (!isDirty) {
      setProject(currentProjectData.data)
      setProjectRole(currentProjectData.role || 'OWNER')
    }
  }, [currentProjectData, currentProjectId, isDirty])

  const value = {
    project,
    isDirty,
    selectedFlow,
    selectedCheckpoint,
    timeView,
    zoom,
    activeView,
    savedProjects,
    currentProjectId,
    projectRole,
    saveStatus,
    saveError,
    setSelectedFlow,
    setSelectedCheckpoint,
    setTimeView,
    setZoom,
    setActiveView,
    setIsDirty,
    setProjectRole,
    updateProject,
    newProject,
    loadProject,
    addFlow,
    updateFlow,
    deleteFlow,
    toggleFlowVisibility,
    addCheckpoint,
    updateCheckpoint,
    deleteCheckpoint,
    addSubtask,
    toggleSubtask,
    updateSubtask,
    deleteSubtask,
    reorderSubtasks,
    reorderCheckpoints,
    addDependency,
    removeDependency,
    // Database operations
    fetchUserProjects,
    saveProjectToDatabase,
    loadProjectFromDatabase,
    deleteProjectFromDatabase
  }

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  )
}

export function useProject() {
  const context = useContext(ProjectContext)
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider')
  }
  return context
}
